import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { gainColor, pct, usd } from "../lib/format";

export const fadeUp = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 8 },
  transition: { duration: 0.25, ease: "easeOut" },
};

export function Panel({ children, className = "", delay = 0, testid }) {
  return (
    <motion.div
      data-testid={testid}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: "easeOut", delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export function GainBadge({ value, suffix = "%", testid }) {
  const positive = value >= 0;
  return (
    <span
      data-testid={testid}
      className={`pill tabular ${positive ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}
    >
      {suffix === "%" ? pct(value) : `${positive ? "+" : ""}${usd(value)}`}
    </span>
  );
}

export function StatCard({ label, value, sub, subColor, secondary, icon: Icon, testid, delay = 0 }) {
  return (
    <Panel delay={delay} className="card p-6 hover:shadow-soft transition-shadow duration-300" testid={testid}>
      <div className="flex items-start justify-between">
        <span className="label">{label}</span>
        {Icon && <Icon className="text-brand-500" size={18} strokeWidth={1.5} />}
      </div>
      <div className="mt-4 text-3xl font-heading font-semibold tracking-tight tabular text-slate-900">{value}</div>
      {secondary && <div className="mt-1 text-sm tabular text-slate-400" data-testid={testid ? `${testid}-home` : undefined}>≈ {secondary}</div>}
      {sub !== undefined && sub !== null && (
        <div className={`mt-1.5 text-sm tabular ${subColor || "text-slate-500"}`}>{sub}</div>
      )}
    </Panel>
  );
}

export function Modal({ open, onClose, title, children, wide = false, testid }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            data-testid={testid}
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            className={`relative bg-white rounded-3xl shadow-2xl border border-slate-200/70 w-full ${wide ? "max-w-2xl" : "max-w-md"} max-h-[90vh] overflow-y-auto`}
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100 sticky top-0 bg-white/90 backdrop-blur-xl z-10">
              <h3 className="text-lg font-heading font-semibold tracking-tight">{title}</h3>
              <button data-testid="modal-close-btn" onClick={onClose} className="text-slate-400 hover:text-slate-700 transition-colors">
                <X size={20} />
              </button>
            </div>
            <div className="p-6">{children}</div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function EmptyState({ title, description, action, image, testid }) {
  return (
    <div data-testid={testid} className="card p-12 flex flex-col items-center text-center">
      {image && <img src={image} alt="" className="w-40 h-40 object-cover rounded-2xl mb-6 opacity-90" />}
      <h3 className="text-xl font-heading font-semibold tracking-tight text-slate-900">{title}</h3>
      <p className="mt-2 text-slate-500 max-w-sm">{description}</p>
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

export function Skeleton({ className = "" }) {
  return <div className={`animate-pulse bg-slate-100 rounded-xl ${className}`} />;
}

export function Field({ label, children, hint }) {
  return (
    <label className="block">
      <span className="label block mb-1.5">{label}</span>
      {children}
      {hint && <span className="block mt-1.5 text-xs text-slate-500 leading-relaxed">{hint}</span>}
    </label>
  );
}

export function Segmented({ options, value, onChange, testid, fullWidthOnMobile = false }) {
  return (
    <div
      data-testid={testid}
      className={`bg-slate-100 rounded-xl p-1 ${fullWidthOnMobile ? "flex w-full sm:inline-flex sm:w-auto" : "inline-flex"}`}
    >
      {options.map((o) => (
        <button
          key={o.value}
          data-testid={`seg-${o.value}`}
          onClick={() => onChange(o.value)}
          className={`px-3 py-1.5 text-sm font-medium rounded-lg whitespace-nowrap transition-colors duration-200 ${
            fullWidthOnMobile ? "flex-1 sm:flex-none" : ""
          } ${value === o.value ? "bg-white text-brand-600 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export const CURRENCIES = ["USD", "EUR", "GBP", "CHF", "JPY", "CAD", "AUD", "INR", "SEK", "NOK"];
